import { SwarmIconClasses } from 'assets/js/enums';
import type { SwarmType } from 'assets/js/enums';
import $ from 'jquery';
import { useEffect } from 'react';
import type { SelectHTMLAttributes } from 'react';
import 'select2';
import 'select2/dist/css/select2.css';
import classNames from '../../utils/classNames';
import FontAwesomeSingleSelection from '../../utils/select2-custom';
import { useMeasuringPointsQuery } from '../graph/components/utils';
import { getSwarmTypeByGraphqlObject } from '../pages/measuringPoint/utils';

export type MeasuringPointOption = ReturnType<
    typeof useMeasuringPointsQuery
>['measuringPoints'][number];
type MeasuringPointSelect2Option = Select2.OptionData | Select2.LoadingData | Select2.IdTextPair;

interface MeasuringPointSelectProps
    extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'onChange' | 'value'> {
    selectId: string;
    onSelectChange: (value: string) => void;
    onReady?: () => void;
    initialValue?: string;
    allowNoSelection?: boolean;
    noSelectionText?: string;
    measuringPoints?: MeasuringPointOption[];
    className?: string;
}

function formatOption(option: MeasuringPointSelect2Option): string | JQuery<HTMLElement> {
    if (!option.id || !option.element) {
        return option.text;
    }
    const { swarmType, detached: detachedString } = option.element.dataset;
    const isDetached = detachedString === 'true';

    return $(`
        <span class="flex items-center">
            <div class="${classNames(
                SwarmIconClasses[swarmType as SwarmType],
                isDetached && 'detached-device',
                'mr-1.5 h-6 w-8'
            )}"></div>
            ${option.text}
        </span>
    `);
}

function getMpDisplayName(measuringPoint: MeasuringPointOption): string {
    const { name, project, deviceName } = measuringPoint;
    let displayName = name;
    const projectName = project?.name;
    if (projectName) {
        displayName = `${projectName} - ${name}`;
    }
    if (deviceName) {
        displayName += ` (${deviceName})`;
    }
    return displayName;
}

function getProjectTemplateExportId(project: MeasuringPointOption['project']): string {
    return project?.canExportProjectTemplate ? project.id : '';
}

// TODO:Remove the usage of jQuery.
// https://github.com/omnidots/website/issues/7770
function MeasuringPointSelect({
    selectId,
    onSelectChange,
    onReady,
    initialValue,
    allowNoSelection = false,
    noSelectionText = gettext('SELECT_MEASURING_POINT'),
    measuringPoints: providedMeasuringPoints,
    className = 'shadow-select',
    ...props
}: MeasuringPointSelectProps): JSX.Element {
    const shouldUseProvidedMeasuringPoints = providedMeasuringPoints !== undefined;
    const { measuringPoints: queriedMeasuringPoints, loading: queryLoading } =
        useMeasuringPointsQuery({
            skip: shouldUseProvidedMeasuringPoints,
        });
    const measuringPoints = providedMeasuringPoints ?? queriedMeasuringPoints;

    useEffect(() => {
        const selectElement = $(`#${selectId}`);
        selectElement.select2({
            width: '100%',
            selectionAdapter: FontAwesomeSingleSelection,
            theme: 'default select2-search',
            templateResult: formatOption,
            templateSelection: formatOption,
        });

        selectElement.on('select2:select', () => {
            onSelectChange(selectElement.val() as string);
        });

        // Cleanup on unmount.
        return () => {
            selectElement.off('select2:select');
            selectElement.select2('destroy');
        };
    }, [selectId, onSelectChange]);

    useEffect(() => {
        if (!queryLoading && onReady) {
            onReady();
        }
    }, [queryLoading, onReady]);

    return (
        <div className={className}>
            <select
                id={selectId}
                aria-label={gettext('SELECT_MEASURING_POINT')}
                {...(initialValue && { value: initialValue })}
                onChange={() => null}
                {...props}
            >
                {allowNoSelection && <option value="">{noSelectionText}</option>}
                {measuringPoints.map((measuringPoint, index) => (
                    <option
                        key={index}
                        value={measuringPoint.id}
                        data-device-name={measuringPoint.deviceName ?? ''}
                        data-detached={(measuringPoint.deviceName === null).toString()}
                        data-swarm-type={getSwarmTypeByGraphqlObject(measuringPoint)}
                        data-permission={measuringPoint.permission ?? ''}
                        data-project-id={getProjectTemplateExportId(measuringPoint.project)}
                    >
                        {getMpDisplayName(measuringPoint)}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default MeasuringPointSelect;
